import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  MenuItem,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  IconButton,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Avatar,
} from "@mui/material";
import { getAuth } from "firebase/auth";
import { FiRefreshCw, FiArrowUp, FiArrowDown, FiEdit2 } from "react-icons/fi";
import { FaTimes } from "react-icons/fa";

/** 🎨 Paleta */
const COLORS = {
  white: "#ffffff",
  whiteSoft: "#fff8ff",
  subtle: "#e6dfef",
  red: "#ff3333",
  redDark: "#cc0000",
  textMain: "#2d233a",
  textMuted: "#6c6478",
};

const API_BASE = "https://vol-backend.onrender.com";

/** 📚 Programas disponibles (hardcoded) */
const PROGRAMAS = [
  { value: "1",  label: "Soy Voluntario" },
  { value: "13", label: "Inscripción PNA" },
  { value: "15", label: "TUM-B Híbrido Nacional" },
];

/** Campos editables */
const EDITABLE_FIELDS = [
  { label: "Nombre",              key: "nombre" },
  { label: "Apellido paterno",    key: "apellido_paterno" },
  { label: "Apellido materno",    key: "apellido_materno" },
  { label: "Fecha de nacimiento", key: "fecha_nacimiento", type: "date" },
  { label: "CURP",                key: "curp" },
  { label: "Sexo",                key: "sexo" },
  { label: "Estado civil",        key: "estado_civil" },
  { label: "Teléfono",            key: "telefono" },
  { label: "Celular",             key: "celular" },
  { label: "Contacto emergencia", key: "emergencia_nombre" },
  { label: "Relación emergencia", key: "emergencia_relacion" },
];

const TABLE_COLUMNS = [
  { key: "matricula",          label: "Matrícula",        minWidth: 120, sortable: true },
  { key: "nombre",             label: "Nombre",           minWidth: 160, sortable: true },
  { key: "apellido_paterno",   label: "Apellido paterno", minWidth: 160, sortable: true },
  { key: "apellido_materno",   label: "Apellido materno", minWidth: 160, sortable: true },
  { key: "curp",               label: "CURP",             minWidth: 180, sortable: true },
  { key: "correo",             label: "Correo",           minWidth: 220, sortable: true },
  { key: "estado",             label: "Estado",           minWidth: 120, sortable: true },
  { key: "entrevistado",       label: "Entrevista",       minWidth: 150, sortable: false },
  { key: "estado_validacion",  label: "Validación",       minWidth: 140, sortable: true },
];

const SEARCH_FIELDS = [
  { value: "matricula", label: "Matrícula" },
  { value: "curp",      label: "CURP" },
  { value: "correo",    label: "Correo" },
];

function safeStr(v) {
  if (v === null || v === undefined) return "";
  return String(v);
}

function normalizeText(s) {
  return safeStr(s).toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
}

function fullNameOf(row) {
  return `${safeStr(row?.nombre)} ${safeStr(row?.apellido_paterno)} ${safeStr(row?.apellido_materno)}`
    .replace(/\s+/g, " ").trim();
}

function toISODateInput(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function EntrevistaChip({ value }) {
  const v = (value || "").toString().trim().toLowerCase();
  if (!v) return (
    <Chip label="—" size="small" variant="outlined"
      sx={{ textTransform: "uppercase", fontSize: 11, opacity: 0.7 }} />
  );
  const isSi = v === "si";
  return (
    <Chip
      label={isSi ? "Entrevistado" : "No entrevistado"}
      size="small"
      color={isSi ? "success" : undefined}
      variant={isSi ? "filled" : "outlined"}
      sx={{ textTransform: "uppercase", fontSize: 11 }}
    />
  );
}

// ── Gráfica donut SVG ─────────────────────────────────────────────
function DonutChart({ porcentaje, inhabilitado }) {
  const r     = 28;
  const circ  = 2 * Math.PI * r;
  const pct   = Math.min(100, Math.max(0, porcentaje || 0));
  const filled = (pct / 100) * circ;
  const color  = inhabilitado
    ? "#9e9e9e"
    : pct >= 80 ? "#2e7d32"
    : pct >= 40 ? "#f57c00"
    : "#c62828";

  return (
    <Box sx={{ position: "relative", width: 72, height: 72, flexShrink: 0 }}>
      <svg width="72" height="72" style={{ transform: "rotate(-90deg)" }}>
        <circle cx="36" cy="36" r={r} fill="none" stroke="#e0e0e0" strokeWidth="8" />
        <circle
          cx="36" cy="36" r={r}
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeDasharray={`${filled} ${circ - filled}`}
          strokeLinecap="round"
        />
      </svg>
      <Box sx={{
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)", textAlign: "center", lineHeight: 1,
      }}>
        <Typography sx={{ fontSize: 12, fontWeight: 900, color: inhabilitado ? "#9e9e9e" : color }}>
          {inhabilitado ? "—" : `${pct}%`}
        </Typography>
      </Box>
    </Box>
  );
}

// ── Componente principal ──────────────────────────────────────────
export default function EditarUsuarios() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  const [currentUserRole, setCurrentUserRole] = useState(null);

  const [searchField, setSearchField] = useState("matricula");
  const [search, setSearch]           = useState("");
  const [nameSearch, setNameSearch]   = useState("");

  const [estadoFilter,     setEstadoFilter]     = useState("");
  const [coloniaFilter,    setColoniaFilter]    = useState("");
  const [validacionFilter, setValidacionFilter] = useState("");
  const [entrevistaFilter, setEntrevistaFilter] = useState("");
  const [programaFilter,   setProgramaFilter]   = useState("");

  const [page, setPage]               = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sortKey, setSortKey]         = useState("matricula");
  const [sortDir, setSortDir]         = useState("asc");

  // ── Modal editor ──
  const [open, setOpen]         = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm]         = useState({});
  const [saving, setSaving]     = useState(false);
  const [saveMsg, setSaveMsg]   = useState("");
  const [saveError, setSaveError] = useState("");

  // ── Tabs ──
  const [activeTab, setActiveTab] = useState(0);

  // ── Progreso ──
  const [progreso, setProgreso]               = useState(null);
  const [progresoLoading, setProgresoLoading] = useState(false);
  const [progresoError, setProgresoError]     = useState("");

  // ── Acciones de validación / eliminación (admin directo) ──
  const [accionDialog,  setAccionDialog]  = useState(null);
  const [accionLoading, setAccionLoading] = useState(false);
  const [accionError,   setAccionError]   = useState("");

  // ── Solicitudes (moderador → admin) ──
  const [solicitudDialog,  setSolicitudDialog]  = useState(null);
  const [solicitudLoading, setSolicitudLoading] = useState(false);
  const [solicitudError,   setSolicitudError]   = useState("");

  // ── Acciones de programa (admin directo) ──
  const [accionProgDialog,  setAccionProgDialog]  = useState(null);
  const [accionProgLoading, setAccionProgLoading] = useState(false);
  const [accionProgError,   setAccionProgError]   = useState("");

  // ── Solicitudes de programa (moderador → admin) ──
  const [solicitudProgDialog,  setSolicitudProgDialog]  = useState(null);
  const [solicitudProgLoading, setSolicitudProgLoading] = useState(false);
  const [solicitudProgError,   setSolicitudProgError]   = useState("");

  const panelRef  = useRef(null);
  const debounceRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────
  // fetchUsers
  // ─────────────────────────────────────────────────────────────────
  const fetchUsers = async ({ field, term, programId } = {}) => {
    setLoading(true);
    setError("");
    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("Usuario no autenticado");
      const idToken = await currentUser.getIdToken();
      const uid     = currentUser.uid;

      try {
        const meRes = await fetch(`${API_BASE}/users/me`, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        const meData = await meRes.json();
        setCurrentUserRole(meData?.rol || null);
      } catch (roleErr) {
        console.warn("No se pudo obtener el rol:", roleErr);
        setCurrentUserRole(null);
      }

      const url = new URL(`${API_BASE}/users`);
      if (term && term.trim() && field) {
        url.searchParams.set("searchField", field);
        url.searchParams.set("search", term.trim());
      }
      if (programId) url.searchParams.set("program_id", programId);

      const res = await fetch(url.toString(), {
        method: "GET",
        headers: { Authorization: `Bearer ${idToken}`, "x-firebase-uid": uid },
      });
      if (!res.ok) { const t = await res.text(); throw new Error(t || "Error al cargar usuarios"); }
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
      setPage(0);
    } catch (e) {
      setError(e.message || "Error al cargar usuarios");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // fetchProgreso — carga al abrir tab Progreso
  // ─────────────────────────────────────────────────────────────────
  const fetchProgreso = async (userId) => {
    setProgresoLoading(true);
    setProgresoError("");
    try {
      const auth = getAuth();
      const cu    = auth.currentUser;
      if (!cu) throw new Error("No autenticado");
      const idToken = await cu.getIdToken();

      const res = await fetch(`${API_BASE}/users/${userId}/progreso`, {
        headers: { Authorization: `Bearer ${idToken}`, "x-firebase-uid": cu.uid },
      });
      if (!res.ok) throw new Error("Error al cargar progreso");
      const data = await res.json();
      setProgreso(data.programas || []);
    } catch (e) {
      setProgresoError(e.message || "Error al cargar progreso");
    } finally {
      setProgresoLoading(false);
    }
  };

  const setEntrevistadoUser = async (userId, entrevistado) => {
    try {
      if (!userId) throw new Error("userId inválido");
      const auth = getAuth();
      const cu   = auth.currentUser;
      if (!cu) throw new Error("Usuario no autenticado");
      const idToken = await cu.getIdToken();
      const v = (entrevistado ?? "").toString().trim().toLowerCase();
      if (!["si", "no"].includes(v)) throw new Error('Valor inválido (usa "si" o "no")');

      const res = await fetch(`${API_BASE}/users/${userId}/entrevistado`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}`, "x-firebase-uid": cu.uid },
        body: JSON.stringify({ entrevistado: v }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "No se pudo actualizar entrevista");

      setRows((prev) => prev.map((u) => (u.id === userId ? { ...u, entrevistado: v } : u)));
      setSelected((prev) => (prev?.id === userId ? { ...prev, entrevistado: v } : prev));
      setSaveMsg(v === "si" ? "✅ Marcado como entrevistado." : "✅ Marcado como NO entrevistado.");
      setSaveError("");
    } catch (e) {
      setSaveError(e.message || "Error al actualizar entrevista");
      setSaveMsg("");
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchUsers({ field: searchField, term: search, programId: programaFilter });
    }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchField, search, programaFilter]);

  const uniqueEstados = useMemo(() => {
    const s = new Set(rows.map((r) => (r.estado || "").trim()).filter(Boolean));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const uniqueColonias = useMemo(() => {
    const s = new Set(rows.map((r) => (r.colonia || "").trim()).filter(Boolean));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const uniqueValidaciones = useMemo(() => {
    const s = new Set(rows.map((r) => (r.estado_validacion || "").trim()).filter(Boolean));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filteredRows = useMemo(() => {
    let r = [...rows];
    if (estadoFilter) {
      const t = normalizeText(estadoFilter);
      r = r.filter((x) => normalizeText(x.estado).includes(t));
    }
    if (coloniaFilter) {
      const t = normalizeText(coloniaFilter);
      r = r.filter((x) => normalizeText(x.colonia).includes(t));
    }
    if (validacionFilter) {
      const t = normalizeText(validacionFilter);
      r = r.filter((x) => normalizeText(x.estado_validacion).includes(t));
    }
    if (entrevistaFilter) {
      const v = (x) => ((x?.entrevistado ?? "").toString().trim().toLowerCase());
      if (entrevistaFilter === "none") r = r.filter((x) => !v(x));
      else r = r.filter((x) => v(x) === entrevistaFilter);
    }
    if (nameSearch.trim()) {
      const t = normalizeText(nameSearch.trim());
      r = r.filter((x) => normalizeText(fullNameOf(x)).includes(t));
    }
    const dir = sortDir === "asc" ? 1 : -1;
    r.sort((a, b) => safeStr(a?.[sortKey]).localeCompare(safeStr(b?.[sortKey])) * dir);
    return r;
  }, [rows, nameSearch, sortKey, sortDir, estadoFilter, coloniaFilter, validacionFilter, entrevistaFilter]);

  const pageRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  // ─────────────────────────────────────────────────────────────────
  // Modal editor
  // ─────────────────────────────────────────────────────────────────
  const openEditor = (row) => {
    setSelected(row);
    setActiveTab(0);     // siempre empieza en Datos personales
    setProgreso(null);   // resetear progreso al abrir nuevo usuario
    setProgresoError("");
    const next = {};
    for (const f of EDITABLE_FIELDS) {
      let v = row?.[f.key] ?? "";
      if (f.type === "date") v = toISODateInput(v);
      next[f.key] = safeStr(v);
    }
    setForm(next);
    setSaveMsg("");
    setSaveError("");
    setOpen(true);
    setTimeout(() => panelRef.current?.focus(), 0);
  };

  const closeEditor = () => {
    if (saving) return;
    setOpen(false);
    setSelected(null);
    setForm({});
    setSaveMsg("");
    setSaveError("");
    setActiveTab(0);
    setProgreso(null);
  };

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    if (newValue === 1 && progreso === null && selected) {
      fetchProgreso(selected.id);
    }
  };

  const computeChanges = () => {
    if (!selected) return {};
    const changes = {};
    for (const f of EDITABLE_FIELDS) {
      const original = selected?.[f.key] ?? "";
      let originalComparable = safeStr(original);
      if (f.type === "date") originalComparable = toISODateInput(original);
      const current = safeStr(form?.[f.key] ?? "");
      if (current !== originalComparable) changes[f.key] = current;
    }
    return changes;
  };

  const saveChanges = async () => {
    setSaving(true);
    setSaveError("");
    setSaveMsg("");
    try {
      if (!selected?.id) throw new Error("No hay usuario seleccionado");
      const changes = computeChanges();
      if (!Object.keys(changes).length) { setSaveMsg("No hay cambios que guardar."); setSaving(false); return; }

      const auth = getAuth();
      const cu   = auth.currentUser;
      if (!cu) throw new Error("Usuario no autenticado");
      const idToken = await cu.getIdToken();

      const res = await fetch(`${API_BASE}/users/${selected.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}`, "x-firebase-uid": cu.uid },
        body: JSON.stringify(changes),
      });
      if (!res.ok) { const t = await res.text(); throw new Error(`Error al guardar: ${t}`); }

      setSaveMsg("✅ Perfil actualizado correctamente.");
      await fetchUsers({ field: searchField, term: search, programId: programaFilter });
    } catch (e) {
      setSaveError(e.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // Acción directa sobre validación (admin / moderador inactivar)
  // ─────────────────────────────────────────────────────────────────
  const ejecutarAccion = async () => {
    if (!selected || !accionDialog) return;
    setAccionLoading(true);
    setAccionError("");
    try {
      const auth = getAuth();
      const cu   = auth.currentUser;
      if (!cu) throw new Error("No autenticado");
      const idToken = await cu.getIdToken();
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
        "x-firebase-uid": cu.uid,
      };

      if (accionDialog.tipo === "eliminar") {
        const res = await fetch(`${API_BASE}/users/${selected.uid}`, {
          method: "DELETE", headers,
          body: JSON.stringify({ motivo: accionDialog.motivo || null }),
        });
        if (!res.ok) { const t = await res.text(); throw new Error(t); }
        setRows((prev) => prev.filter((u) => u.uid !== selected.uid));
        setAccionDialog(null);
        closeEditor();
        setSaveMsg("✅ Usuario eliminado correctamente.");
      } else {
        const estadoMap = { activar: "activo", inactivar: "inactivo", rechazar: "rechazado" };
        const nuevoEstado = estadoMap[accionDialog.tipo];
        const res = await fetch(`${API_BASE}/users/${selected.uid}/validacion`, {
          method: "PATCH", headers,
          body: JSON.stringify({ estado_validacion: nuevoEstado, motivo: accionDialog.motivo || null }),
        });
        if (!res.ok) { const t = await res.text(); throw new Error(t); }
        setRows((prev) => prev.map((u) => u.uid === selected.uid ? { ...u, estado_validacion: nuevoEstado } : u));
        setSelected((prev) => prev ? { ...prev, estado_validacion: nuevoEstado } : prev);
        setAccionDialog(null);
        const label = accionDialog.tipo === "activar" ? "activado" : accionDialog.tipo === "inactivar" ? "inactivado" : "rechazado";
        setSaveMsg(`✅ Usuario ${label} correctamente.`);
        setSaveError("");
      }
    } catch (e) {
      setAccionError(e.message || "Error al ejecutar acción");
    } finally {
      setAccionLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // Solicitud (moderador → admin) — tipo usuario
  // ─────────────────────────────────────────────────────────────────
  const enviarSolicitud = async () => {
    if (!selected || !solicitudDialog) return;
    if (!solicitudDialog.motivo?.trim()) { setSolicitudError("El motivo es obligatorio para enviar la solicitud."); return; }
    setSolicitudLoading(true);
    setSolicitudError("");
    try {
      const auth = getAuth();
      const cu   = auth.currentUser;
      if (!cu) throw new Error("No autenticado");
      const idToken = await cu.getIdToken();

      const res = await fetch(`${API_BASE}/users/${selected.uid}/solicitud`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}`, "x-firebase-uid": cu.uid },
        body: JSON.stringify({ tipo: solicitudDialog.tipo, motivo: solicitudDialog.motivo.trim() }),
      });
      if (!res.ok) { const t = await res.text(); throw new Error(t); }
      const tipoEnviado = solicitudDialog.tipo;
      setSolicitudDialog(null);
      setSaveMsg(`✅ Solicitud de ${tipoEnviado} enviada. Un administrador la revisará.`);
      setSaveError("");
    } catch (e) {
      setSolicitudError(e.message || "Error al enviar solicitud");
    } finally {
      setSolicitudLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // Acción directa sobre programa (admin)
  // ─────────────────────────────────────────────────────────────────
  const ejecutarAccionPrograma = async () => {
    if (!selected || !accionProgDialog) return;
    setAccionProgLoading(true);
    setAccionProgError("");
    try {
      const auth = getAuth();
      const cu   = auth.currentUser;
      if (!cu) throw new Error("No autenticado");
      const idToken = await cu.getIdToken();
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
        "x-firebase-uid": cu.uid,
      };

      if (accionProgDialog.tipo === "inhabilitar") {
        const res = await fetch(
          `${API_BASE}/users/${selected.id}/programas/${accionProgDialog.programa_id}/inhabilitar`,
          { method: "PATCH", headers }
        );
        if (!res.ok) { const t = await res.text(); throw new Error(t); }
        setProgreso((prev) => prev?.map((p) =>
          p.program_id === accionProgDialog.programa_id ? { ...p, inhabilitado: 1 } : p
        ) ?? null);
        setSaveMsg(`✅ Usuario inhabilitado del programa "${accionProgDialog.programa_nombre}".`);
      } else if (accionProgDialog.tipo === "activar") {
        // ── CAMBIO 1: reactivar programa inhabilitado (admin directo) ──
        const res = await fetch(
          `${API_BASE}/users/${selected.id}/programas/${accionProgDialog.programa_id}/activar`,
          { method: "PATCH", headers }
        );
        if (!res.ok) { const t = await res.text(); throw new Error(t); }
        setProgreso((prev) => prev?.map((p) =>
          p.program_id === accionProgDialog.programa_id ? { ...p, inhabilitado: 0 } : p
        ) ?? null);
        setSaveMsg(`✅ Acceso al programa "${accionProgDialog.programa_nombre}" reactivado.`);
      } else {
        // baja
        const res = await fetch(
          `${API_BASE}/users/${selected.id}/programas/${accionProgDialog.programa_id}/baja`,
          { method: "DELETE", headers }
        );
        if (!res.ok) { const t = await res.text(); throw new Error(t); }
        setProgreso((prev) => prev?.filter((p) => p.program_id !== accionProgDialog.programa_id) ?? null);
        setSaveMsg(`✅ Usuario dado de baja del programa "${accionProgDialog.programa_nombre}".`);
      }
      setAccionProgDialog(null);
    } catch (e) {
      setAccionProgError(e.message || "Error al ejecutar acción sobre programa");
    } finally {
      setAccionProgLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // Solicitud sobre programa (moderador → admin)
  // ─────────────────────────────────────────────────────────────────
  const enviarSolicitudPrograma = async () => {
    if (!selected || !solicitudProgDialog) return;
    if (!solicitudProgDialog.motivo?.trim()) { setSolicitudProgError("El motivo es obligatorio."); return; }
    setSolicitudProgLoading(true);
    setSolicitudProgError("");
    try {
      const auth = getAuth();
      const cu   = auth.currentUser;
      if (!cu) throw new Error("No autenticado");
      const idToken = await cu.getIdToken();

      const res = await fetch(`${API_BASE}/users/${selected.uid}/solicitud`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}`, "x-firebase-uid": cu.uid },
        body: JSON.stringify({
          tipo: solicitudProgDialog.tipo,
          motivo: solicitudProgDialog.motivo.trim(),
          programa_id: solicitudProgDialog.programa_id,
        }),
      });
      if (!res.ok) { const t = await res.text(); throw new Error(t); }
      // ── CAMBIO 2: etiqueta descriptiva para cada tipo de solicitud ──
      const label = solicitudProgDialog.tipo === "inhabilitar_programa"
        ? "inhabilitación"
        : solicitudProgDialog.tipo === "activar_programa"
        ? "reactivación"
        : "baja";
      setSolicitudProgDialog(null);
      setSaveMsg(`✅ Solicitud de ${label} del programa enviada. Un administrador la revisará.`);
      setSaveError("");
    } catch (e) {
      setSolicitudProgError(e.message || "Error al enviar solicitud");
    } finally {
      setSolicitudProgLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* ── Header + filtros ── */}
      <Paper
        elevation={0}
        sx={{
          p: 2, borderRadius: 3, mb: 2,
          background: `linear-gradient(180deg, ${COLORS.whiteSoft} 0%, ${COLORS.white} 100%)`,
          border: `1px solid ${COLORS.subtle}`,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }} spacing={1.5}
          alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900, color: COLORS.textMain, letterSpacing: 1.2 }}>
              MODIFICAR USUARIOS
            </Typography>
            <Typography sx={{ color: COLORS.textMuted, mt: 0.3 }}>
              Busca, abre un usuario y edita sus campos.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip
              label={`${filteredRows.length} resultados`}
              sx={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.subtle}`, fontWeight: 800, color: COLORS.textMain }}
            />
            <Tooltip title="Refrescar">
              <IconButton
                onClick={() => fetchUsers({ field: searchField, term: search, programId: programaFilter })}
                sx={{ border: `1px solid ${COLORS.subtle}`, borderRadius: 2, backgroundColor: COLORS.white }}
              >
                <FiRefreshCw />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Divider sx={{ my: 1.5, borderColor: COLORS.subtle }} />

        <Stack spacing={1.2}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} alignItems="stretch">
            <TextField select size="small" label="Buscar por" value={searchField}
              onChange={(e) => setSearchField(e.target.value)}
              sx={{ width: { xs: "100%", md: 220 }, backgroundColor: COLORS.white }}>
              {SEARCH_FIELDS.map((f) => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Búsqueda" placeholder="Matrícula / CURP / Correo"
              value={search} onChange={(e) => setSearch(e.target.value)}
              sx={{ width: { xs: "100%", md: 520 }, backgroundColor: COLORS.white }} />
            <TextField size="small" label="Nombre / apellidos" placeholder="Filtro extra (client-side)"
              value={nameSearch} onChange={(e) => setNameSearch(e.target.value)}
              sx={{ flex: 1, minWidth: { md: 360 }, backgroundColor: COLORS.white }} />
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} alignItems="stretch" sx={{ mt: 1.2 }}>
            <TextField select size="small" label="Estado" value={estadoFilter}
              onChange={(e) => setEstadoFilter(e.target.value)}
              sx={{ width: { xs: "100%", md: 260 }, backgroundColor: COLORS.white }}>
              <MenuItem value="">Todos</MenuItem>
              {uniqueEstados.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Colonia" value={coloniaFilter}
              onChange={(e) => setColoniaFilter(e.target.value)}
              sx={{ width: { xs: "100%", md: 320 }, backgroundColor: COLORS.white }}>
              <MenuItem value="">Todas</MenuItem>
              {uniqueColonias.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Validación" value={validacionFilter}
              onChange={(e) => setValidacionFilter(e.target.value)}
              sx={{ width: { xs: "100%", md: 220 }, backgroundColor: COLORS.white }}>
              <MenuItem value="">Todas</MenuItem>
              {uniqueValidaciones.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Entrevista" value={entrevistaFilter}
              onChange={(e) => setEntrevistaFilter(e.target.value)}
              sx={{ width: { xs: "100%", md: 220 }, backgroundColor: COLORS.white }}>
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="si">Entrevistado</MenuItem>
              <MenuItem value="no">No entrevistado</MenuItem>
              <MenuItem value="none">Sin dato</MenuItem>
            </TextField>
            <TextField select size="small" label="Programa" value={programaFilter}
              onChange={(e) => { setProgramaFilter(e.target.value); setPage(0); }}
              sx={{ width: { xs: "100%", md: 260 }, backgroundColor: COLORS.white }}>
              <MenuItem value="">Todos</MenuItem>
              {PROGRAMAS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
            </TextField>
            <Button variant="text"
              onClick={() => { setEstadoFilter(""); setColoniaFilter(""); setValidacionFilter(""); setEntrevistaFilter(""); setProgramaFilter(""); setPage(0); }}
              sx={{ width: { xs: "100%", md: 220 }, borderRadius: 2, textTransform: "none", fontWeight: 900,
                color: COLORS.textMuted, backgroundColor: COLORS.white, border: `1px solid ${COLORS.subtle}`,
                "&:hover": { backgroundColor: COLORS.whiteSoft } }}>
              Limpiar filtros
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ borderRadius: 3, mb: 2 }}>{error}</Alert>}

      {/* ── Tabla ── */}
      <Paper elevation={0} sx={{ borderRadius: 3, backgroundColor: COLORS.white, border: `1px solid ${COLORS.subtle}`, overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: "70vh" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {TABLE_COLUMNS.map((c) => (
                  <TableCell key={c.key} onClick={() => c.sortable && handleSort(c.key)}
                    sx={{ minWidth: c.minWidth, backgroundColor: COLORS.whiteSoft, borderBottom: `1px solid ${COLORS.subtle}`,
                      fontWeight: 900, color: COLORS.textMain, cursor: c.sortable ? "pointer" : "default",
                      userSelect: "none", whiteSpace: "nowrap" }}>
                    <Stack direction="row" spacing={0.7} alignItems="center">
                      <span>{c.label}</span>
                      {sortKey === c.key ? (sortDir === "asc" ? <FiArrowUp /> : <FiArrowDown />) : null}
                    </Stack>
                  </TableCell>
                ))}
                <TableCell sx={{ minWidth: 140, backgroundColor: COLORS.whiteSoft, borderBottom: `1px solid ${COLORS.subtle}`,
                  fontWeight: 900, color: COLORS.textMain, whiteSpace: "nowrap" }}>
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={TABLE_COLUMNS.length + 1} sx={{ py: 6 }}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                      <CircularProgress size={20} />
                      <Typography variant="body2" sx={{ color: COLORS.textMuted }}>Cargando usuarios…</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : pageRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={TABLE_COLUMNS.length + 1} sx={{ py: 6 }}>
                    <Typography variant="body2" sx={{ color: COLORS.textMuted, textAlign: "center" }}>
                      No hay resultados con esos filtros.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pageRows.map((r, idx) => (
                  <TableRow key={`${r.id ?? "row"}-${idx}`} hover sx={{ "& td": { borderBottom: `1px solid ${COLORS.subtle}` } }}>
                    {TABLE_COLUMNS.map((c) => (
                      <TableCell key={c.key} sx={{ whiteSpace: "nowrap" }}>
                        {c.key === "entrevistado"
                          ? <EntrevistaChip value={r?.entrevistado} />
                          : safeStr(r?.[c.key]) ? safeStr(r?.[c.key]) : <span style={{ color: COLORS.textMuted }}>—</span>
                        }
                      </TableCell>
                    ))}
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Button size="small" variant="contained" startIcon={<FiEdit2 />} onClick={() => openEditor(r)}
                        sx={{ backgroundColor: COLORS.red, "&:hover": { backgroundColor: COLORS.redDark },
                          borderRadius: 2, fontWeight: 900, textTransform: "none" }}>
                        Editar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={filteredRows.length} page={page}
          onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50, 100]} />
      </Paper>

      {/* ══════════════════════════════════════════════════════════════
           MODAL EDITOR — con Tabs
      ══════════════════════════════════════════════════════════════ */}
      <Dialog
        open={open} onClose={closeEditor} fullWidth maxWidth="lg"
        PaperProps={{
          sx: { borderRadius: 3, border: `1px solid ${COLORS.subtle}`,
            background: `linear-gradient(180deg, ${COLORS.whiteSoft} 0%, ${COLORS.white} 100%)` },
        }}
      >
        <DialogTitle sx={{ fontWeight: 900, color: COLORS.textMain, pb: 0 }}>
          Editar usuario
          <IconButton onClick={closeEditor} disabled={saving}
            sx={{ position: "absolute", right: 12, top: 12, border: `1px solid ${COLORS.subtle}`, borderRadius: 2 }}>
            <FaTimes />
          </IconButton>
        </DialogTitle>

        {/* ── Tabs ── */}
        <Box sx={{ px: 3, pt: 1 }}>
          <Tabs
            value={activeTab} onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": { textTransform: "none", fontWeight: 700, color: COLORS.textMuted },
              "& .Mui-selected": { color: COLORS.red + " !important" },
              "& .MuiTabs-indicator": { backgroundColor: COLORS.red },
            }}
          >
            <Tab label="Datos personales" />
            <Tab label="Progreso" />
          </Tabs>
        </Box>

        <DialogContent dividers sx={{ borderColor: COLORS.subtle }}>
          <Box tabIndex={-1} ref={panelRef} />

          {/* ════ TAB 0: Datos personales ════ */}
          {activeTab === 0 && (
            <>
              {/* Sección identidad */}
              {selected && (
                <Paper elevation={0} sx={{ p: 1.5, mb: 2, borderRadius: 3, backgroundColor: COLORS.white, border: `1px solid ${COLORS.subtle}` }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1 }}>
                    {/* Foto de perfil o iniciales */}
                    <Avatar
                      src={selected.photo_url || ""}
                      sx={{
                        width: 56, height: 56,
                        bgcolor: COLORS.red,
                        color: "#fff",
                        fontWeight: 900,
                        fontSize: 20,
                        border: `2px solid ${COLORS.subtle}`,
                        flexShrink: 0,
                      }}
                    >
                      {!selected.photo_url && (
                        `${(selected.nombre || "?")[0]}${(selected.apellido_pat || "")[0] || ""}`.toUpperCase()
                      )}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 900, color: COLORS.textMain, mb: 0.5 }}>Identidad</Typography>
                  <Typography component="div" sx={{ color: COLORS.textMuted, fontSize: 13 }}>
                    Matrícula: <b style={{ color: COLORS.textMain }}>{safeStr(selected.matricula) || "—"}</b> &nbsp;•&nbsp;
                    Correo: <b style={{ color: COLORS.textMain }}>{safeStr(selected.correo) || "—"}</b> &nbsp;•&nbsp;
                    Validación: <b style={{ color: COLORS.textMain }}>{safeStr(selected.estado_validacion) || "—"}</b>
                    &nbsp;•&nbsp; Entrevista: <b style={{ color: COLORS.textMain }}><EntrevistaChip value={selected.entrevistado} /></b>
                  </Typography>

                  <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                    <Button size="small" variant="contained"
                      onClick={() => setEntrevistadoUser(selected.id, "si")}
                      sx={{ backgroundColor: COLORS.red, "&:hover": { backgroundColor: COLORS.redDark },
                        borderRadius: 2, fontWeight: 900, textTransform: "none" }}>
                      Marcar SI entrevistado
                    </Button>
                    <Button size="small" variant="outlined"
                      onClick={() => setEntrevistadoUser(selected.id, "no")}
                      sx={{ borderRadius: 2, textTransform: "none", fontWeight: 900,
                        borderColor: COLORS.subtle, color: COLORS.textMain, backgroundColor: COLORS.white,
                        "&:hover": { backgroundColor: COLORS.whiteSoft } }}>
                      Marcar NO entrevistado
                    </Button>
                  </Stack>
                    </Box>{/* fin Box derecho */}
                  </Box>{/* fin Box flex avatar+info */}

                  <Divider sx={{ my: 1.5, borderColor: COLORS.subtle }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted, mb: 0.75 }}>
                    Estado de validación: <b style={{ color: COLORS.textMain }}>{safeStr(selected.estado_validacion) || "—"}</b>
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {/* ── Activar (todos los roles) ── */}
                    <Button size="small" variant="contained"
                      disabled={selected.estado_validacion === "activo"}
                      onClick={() => setAccionDialog({ tipo: "activar", motivo: "" })}
                      sx={{ backgroundColor: "#1b5e20", "&:hover": { backgroundColor: "#145214" },
                        "&.Mui-disabled": { backgroundColor: "#ccc" },
                        borderRadius: 2, fontWeight: 900, textTransform: "none" }}>
                      ✓ Activar
                    </Button>

                    {/* ── Inactivar — admin: directo | moderador: también directo ── */}
                    <Button size="small" variant="outlined"
                      disabled={selected.estado_validacion === "inactivo"}
                      onClick={() => setAccionDialog({ tipo: "inactivar", motivo: "" })}
                      sx={{ borderRadius: 2, fontWeight: 900, textTransform: "none",
                        borderColor: "#e65100", color: "#e65100",
                        "&:hover": { backgroundColor: "#fff3e0" },
                        "&.Mui-disabled": { borderColor: "#ccc", color: "#ccc" } }}>
                      ⊘ Inactivar usuario
                    </Button>

                    {/* ── Rechazar — solo admin ── */}
                    {currentUserRole !== "moderador" && (
                      <Button size="small" variant="outlined"
                        disabled={selected.estado_validacion === "rechazado"}
                        onClick={() => setAccionDialog({ tipo: "rechazar", motivo: "" })}
                        sx={{ borderRadius: 2, fontWeight: 900, textTransform: "none",
                          borderColor: "#b00020", color: "#b00020",
                          "&:hover": { backgroundColor: "#ffebee" },
                          "&.Mui-disabled": { borderColor: "#ccc", color: "#ccc" } }}>
                        ✗ Rechazar
                      </Button>
                    )}

                    {/* ── Eliminar — admin directo | moderador: solicitud ── */}
                    {currentUserRole === "moderador" ? (
                      <Button size="small" variant="outlined"
                        onClick={() => setSolicitudDialog({ tipo: "eliminacion", motivo: "" })}
                        sx={{ borderRadius: 2, fontWeight: 900, textTransform: "none",
                          borderColor: "#b00020", color: "#b00020",
                          "&:hover": { backgroundColor: "#ffebee" } }}>
                        🗑 Solicitar eliminación
                      </Button>
                    ) : (
                      <Button size="small" variant="outlined"
                        onClick={() => setAccionDialog({ tipo: "eliminar", motivo: "" })}
                        sx={{ borderRadius: 2, fontWeight: 900, textTransform: "none",
                          borderColor: "#b00020", color: "#b00020",
                          "&:hover": { backgroundColor: "#ffebee" } }}>
                        🗑 Eliminar usuario
                      </Button>
                    )}
                  </Stack>
                </Paper>
              )}

              {saveError && <Alert severity="error" sx={{ borderRadius: 3, mb: 2 }}>{saveError}</Alert>}
              {saveMsg   && <Alert severity="success" sx={{ borderRadius: 3, mb: 2 }}>{saveMsg}</Alert>}

              {/* Campos editables */}
              <Typography sx={{ fontWeight: 700, color: COLORS.textMuted, fontSize: 12, mb: 1 }}>
                Datos personales
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.2 }}>
                {EDITABLE_FIELDS.map((f) => (
                  <TextField key={f.key} size="small" label={f.label} type={f.type || "text"}
                    value={form?.[f.key] ?? ""}
                    onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                    sx={{ backgroundColor: COLORS.white }}
                    InputLabelProps={f.type === "date" ? { shrink: true } : undefined}
                  />
                ))}
              </Box>
            </>
          )}

          {/* ════ TAB 1: Progreso ════ */}
          {activeTab === 1 && (
            <Box sx={{ pt: 0.5 }}>
              {progresoLoading && (
                <Stack alignItems="center" spacing={1} sx={{ py: 5 }}>
                  <CircularProgress size={30} sx={{ color: COLORS.red }} />
                  <Typography sx={{ color: COLORS.textMuted, fontSize: 13 }}>Cargando progreso…</Typography>
                </Stack>
              )}

              {progresoError && !progresoLoading && (
                <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{progresoError}</Alert>
              )}

              {saveMsg && !progresoLoading && (
                <Alert severity="success" sx={{ borderRadius: 2, mb: 2 }}>{saveMsg}</Alert>
              )}

              {!progresoLoading && progreso?.length === 0 && (
                <Box sx={{ textAlign: "center", py: 5 }}>
                  <Typography sx={{ color: COLORS.textMuted }}>
                    Este usuario no está inscrito en ningún programa.
                  </Typography>
                </Box>
              )}

              {!progresoLoading && progreso && progreso.length > 0 && (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                  {progreso.map((prog) => (
                    <Paper
                      key={prog.program_id}
                      elevation={0}
                      sx={{
                        p: 2, borderRadius: 3,
                        border: `1px solid ${prog.inhabilitado ? "#ffcdd2" : COLORS.subtle}`,
                        backgroundColor: prog.inhabilitado ? "#fff8f8" : COLORS.white,
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <DonutChart porcentaje={prog.porcentaje} inhabilitado={prog.inhabilitado} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" sx={{ mb: 0.4 }}>
                            <Typography sx={{ fontWeight: 900, color: COLORS.textMain, fontSize: 14,
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}>
                              {prog.programa_nombre}
                            </Typography>
                            {!!prog.inhabilitado && (
                              <Chip label="Inhabilitado" size="small"
                                sx={{ backgroundColor: "#ffcdd2", color: "#c62828", fontSize: 10, fontWeight: 700, height: 18 }} />
                            )}
                          </Stack>
                          <Typography sx={{ fontSize: 12, color: COLORS.textMuted }}>
                            {prog.bloques_aprobados} / {prog.total_bloques} bloques aprobados
                          </Typography>
                          {prog.code && (
                            <Typography sx={{ fontSize: 11, color: COLORS.textMuted, mt: 0.2 }}>
                              Cód: <b>{prog.code}</b>
                            </Typography>
                          )}
                        </Box>
                      </Stack>

                      {/* Botones de acción por programa — excluido programa "Soy Voluntario" (SV) */}
                      {prog.code !== "SV" && (
                        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap">
                          {currentUserRole === "admin" ? (
                            <>
                              {/* ── CAMBIO 3: admin — toggle Inhabilitar / Reactivar ── */}
                              {!!prog.inhabilitado ? (
                                <Button size="small" variant="outlined"
                                  onClick={() => setAccionProgDialog({
                                    tipo: "activar",
                                    programa_id: prog.program_id,
                                    programa_nombre: prog.programa_nombre,
                                  })}
                                  sx={{ fontSize: 11, borderRadius: 2, textTransform: "none", fontWeight: 700,
                                    borderColor: "#1b5e20", color: "#1b5e20",
                                    "&:hover": { backgroundColor: "#e8f5e9" } }}>
                                  ✓ Reactivar acceso
                                </Button>
                              ) : (
                                <Button size="small" variant="outlined"
                                  onClick={() => setAccionProgDialog({
                                    tipo: "inhabilitar",
                                    programa_id: prog.program_id,
                                    programa_nombre: prog.programa_nombre,
                                  })}
                                  sx={{ fontSize: 11, borderRadius: 2, textTransform: "none", fontWeight: 700,
                                    borderColor: "#e65100", color: "#e65100",
                                    "&:hover": { backgroundColor: "#fff3e0" } }}>
                                  ⊘ Inhabilitar
                                </Button>
                              )}
                              <Button size="small" variant="outlined"
                                onClick={() => setAccionProgDialog({
                                  tipo: "baja",
                                  programa_id: prog.program_id,
                                  programa_nombre: prog.programa_nombre,
                                })}
                                sx={{ fontSize: 11, borderRadius: 2, textTransform: "none", fontWeight: 700,
                                  borderColor: "#b00020", color: "#b00020",
                                  "&:hover": { backgroundColor: "#ffebee" } }}>
                                🗑 Dar de baja
                              </Button>
                            </>
                          ) : (
                            <>
                              {/* ── CAMBIO 3: moderador — toggle Solicitar inhabilitar / Solicitar activar ── */}
                              {!!prog.inhabilitado ? (
                                <Button size="small" variant="outlined"
                                  onClick={() => setSolicitudProgDialog({
                                    tipo: "activar_programa",
                                    programa_id: prog.program_id,
                                    programa_nombre: prog.programa_nombre,
                                    motivo: "",
                                  })}
                                  sx={{ fontSize: 11, borderRadius: 2, textTransform: "none", fontWeight: 700,
                                    borderColor: "#1b5e20", color: "#1b5e20",
                                    "&:hover": { backgroundColor: "#e8f5e9" } }}>
                                  ✓ Solicitar activar
                                </Button>
                              ) : (
                                <Button size="small" variant="outlined"
                                  onClick={() => setSolicitudProgDialog({
                                    tipo: "inhabilitar_programa",
                                    programa_id: prog.program_id,
                                    programa_nombre: prog.programa_nombre,
                                    motivo: "",
                                  })}
                                  sx={{ fontSize: 11, borderRadius: 2, textTransform: "none", fontWeight: 700,
                                    borderColor: "#e65100", color: "#e65100",
                                    "&:hover": { backgroundColor: "#fff3e0" } }}>
                                  ⊘ Solicitar inhabilitar
                                </Button>
                              )}
                              <Button size="small" variant="outlined"
                                onClick={() => setSolicitudProgDialog({
                                  tipo: "baja_programa",
                                  programa_id: prog.program_id,
                                  programa_nombre: prog.programa_nombre,
                                  motivo: "",
                                })}
                                sx={{ fontSize: 11, borderRadius: 2, textTransform: "none", fontWeight: 700,
                                  borderColor: "#b00020", color: "#b00020",
                                  "&:hover": { backgroundColor: "#ffebee" } }}>
                                🗑 Solicitar dar de baja
                              </Button>
                            </>
                          )}
                        </Stack>
                      )}
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        {/* Solo muestra "Guardar cambios" en tab Datos personales */}
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeEditor} disabled={saving} variant="outlined"
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 900,
              borderColor: COLORS.subtle, color: COLORS.textMain, backgroundColor: COLORS.white,
              "&:hover": { backgroundColor: COLORS.whiteSoft } }}>
            Cancelar
          </Button>
          {activeTab === 0 && (
            <Button onClick={saveChanges} disabled={saving || !selected} variant="contained"
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 900,
                backgroundColor: COLORS.red, "&:hover": { backgroundColor: COLORS.redDark } }}>
              {saving ? "Guardando…" : "Guardar cambios"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Dialog confirmación acción validación ── */}
      <Dialog open={!!accionDialog} onClose={() => { setAccionDialog(null); setAccionError(""); }}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 900, color: COLORS.textMain }}>
          {accionDialog?.tipo === "activar"   && "✓ Activar usuario"}
          {accionDialog?.tipo === "inactivar" && "⊘ Inactivar usuario"}
          {accionDialog?.tipo === "rechazar"  && "✗ Rechazar usuario"}
          {accionDialog?.tipo === "eliminar"  && "🗑 Eliminar usuario"}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: COLORS.textMain, mb: 1.5 }}>
            {accionDialog?.tipo === "eliminar"
              ? <>¿Estás seguro que deseas <b>eliminar permanentemente</b> a <b>{selected && `${safeStr(selected.nombre)} ${safeStr(selected.apellido_paterno)}`}</b>? Esta acción no se puede deshacer.</>
              : <>¿Cambiar el estado de <b>{selected && `${safeStr(selected.nombre)} ${safeStr(selected.apellido_paterno)}`}</b> a <b>{accionDialog?.tipo === "activar" ? "activo" : accionDialog?.tipo === "inactivar" ? "inactivo" : "rechazado"}</b>?</>
            }
          </Typography>
          <TextField
            label={accionDialog?.tipo === "eliminar" ? "Motivo de eliminación (opcional)" : "Motivo (opcional)"}
            multiline rows={2} fullWidth size="small"
            value={accionDialog?.motivo || ""}
            onChange={(e) => setAccionDialog((prev) => ({ ...prev, motivo: e.target.value }))}
            placeholder="Ej: Datos incorrectos, duplicado, solicitud del usuario..." />
          {accionError && <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>{accionError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => { setAccionDialog(null); setAccionError(""); }} disabled={accionLoading}
            sx={{ textTransform: "none", color: COLORS.textMuted }}>Cancelar</Button>
          <Button variant="contained" onClick={ejecutarAccion} disabled={accionLoading}
            sx={{ backgroundColor: accionDialog?.tipo === "activar" ? "#1b5e20" : "#b00020",
              "&:hover": { backgroundColor: accionDialog?.tipo === "activar" ? "#145214" : "#8b0000" },
              textTransform: "none", fontWeight: 700, borderRadius: 999 }}>
            {accionLoading && <CircularProgress size={14} sx={{ color: "#fff", mr: 1 }} />}
            {accionDialog?.tipo === "activar"   && "Sí, activar"}
            {accionDialog?.tipo === "inactivar" && "Sí, inactivar"}
            {accionDialog?.tipo === "rechazar"  && "Sí, rechazar"}
            {accionDialog?.tipo === "eliminar"  && "Sí, eliminar permanentemente"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog confirmación solicitud usuario (moderador) ── */}
      <Dialog open={!!solicitudDialog} onClose={() => { setSolicitudDialog(null); setSolicitudError(""); }}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 900, color: COLORS.textMain }}>
          {solicitudDialog?.tipo === "inactivacion" && "⊘ Solicitar inactivación"}
          {solicitudDialog?.tipo === "eliminacion"  && "🗑 Solicitar eliminación"}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: COLORS.textMain, mb: 1.5 }}>
            Tu solicitud será enviada a un administrador para su revisión. Describe el motivo.
          </Typography>
          <TextField label="Motivo (obligatorio)" multiline rows={3} fullWidth size="small" required
            value={solicitudDialog?.motivo || ""}
            onChange={(e) => setSolicitudDialog((prev) => ({ ...prev, motivo: e.target.value }))}
            placeholder="Ej: El usuario solicitó darse de baja, datos duplicados..." error={!!solicitudError} />
          {solicitudError && <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>{solicitudError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => { setSolicitudDialog(null); setSolicitudError(""); }} disabled={solicitudLoading}
            sx={{ textTransform: "none", color: COLORS.textMuted }}>Cancelar</Button>
          <Button variant="contained" onClick={enviarSolicitud} disabled={solicitudLoading}
            sx={{ backgroundColor: "#b00020", "&:hover": { backgroundColor: "#8b0000" },
              textTransform: "none", fontWeight: 700, borderRadius: 999 }}>
            {solicitudLoading && <CircularProgress size={14} sx={{ color: "#fff", mr: 1 }} />}
            Enviar solicitud
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog confirmación acción programa (admin directo) ── */}
      <Dialog open={!!accionProgDialog} onClose={() => { setAccionProgDialog(null); setAccionProgError(""); }}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        {/* ── CAMBIO 4: título, descripción y botón para "activar" ── */}
        <DialogTitle sx={{ fontWeight: 900, color: COLORS.textMain }}>
          {accionProgDialog?.tipo === "activar"    && "✓ Reactivar acceso al programa"}
          {accionProgDialog?.tipo === "inhabilitar" && "⊘ Inhabilitar del programa"}
          {accionProgDialog?.tipo === "baja"        && "🗑 Dar de baja del programa"}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: COLORS.textMain, mb: 1 }}>
            {accionProgDialog?.tipo === "activar"
              ? <>¿Reactivar el acceso de <b>{selected && `${safeStr(selected.nombre)} ${safeStr(selected.apellido_paterno)}`}</b> al programa <b>{accionProgDialog?.programa_nombre}</b>? El usuario podrá volver a ingresar y continuar su avance.</>
              : accionProgDialog?.tipo === "inhabilitar"
              ? <>¿Inhabilitar a <b>{selected && `${safeStr(selected.nombre)} ${safeStr(selected.apellido_paterno)}`}</b> del programa <b>{accionProgDialog?.programa_nombre}</b>? El acceso y avance quedarán bloqueados pero los datos se conservan.</>
              : <>¿Dar de baja a <b>{selected && `${safeStr(selected.nombre)} ${safeStr(selected.apellido_paterno)}`}</b> del programa <b>{accionProgDialog?.programa_nombre}</b>? Se <b>eliminará todo el progreso</b>. Esta acción no se puede deshacer.</>
            }
          </Typography>
          {accionProgError && <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>{accionProgError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => { setAccionProgDialog(null); setAccionProgError(""); }} disabled={accionProgLoading}
            sx={{ textTransform: "none", color: COLORS.textMuted }}>Cancelar</Button>
          <Button variant="contained" onClick={ejecutarAccionPrograma} disabled={accionProgLoading}
            sx={{
              backgroundColor: accionProgDialog?.tipo === "activar" ? "#1b5e20" : accionProgDialog?.tipo === "inhabilitar" ? "#e65100" : "#b00020",
              "&:hover": { backgroundColor: accionProgDialog?.tipo === "activar" ? "#145214" : accionProgDialog?.tipo === "inhabilitar" ? "#bf360c" : "#8b0000" },
              textTransform: "none", fontWeight: 700, borderRadius: 999 }}>
            {accionProgLoading && <CircularProgress size={14} sx={{ color: "#fff", mr: 1 }} />}
            {accionProgDialog?.tipo === "activar"    && "Sí, reactivar acceso"}
            {accionProgDialog?.tipo === "inhabilitar" && "Sí, inhabilitar"}
            {accionProgDialog?.tipo === "baja"        && "Sí, dar de baja permanentemente"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog solicitud programa (moderador) ── */}
      <Dialog open={!!solicitudProgDialog} onClose={() => { setSolicitudProgDialog(null); setSolicitudProgError(""); }}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        {/* ── CAMBIO 5: título y descripción para "activar_programa" ── */}
        <DialogTitle sx={{ fontWeight: 900, color: COLORS.textMain }}>
          {solicitudProgDialog?.tipo === "activar_programa"    && "✓ Solicitar reactivación del programa"}
          {solicitudProgDialog?.tipo === "inhabilitar_programa" && "⊘ Solicitar inhabilitación del programa"}
          {solicitudProgDialog?.tipo === "baja_programa"        && "🗑 Solicitar baja del programa"}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: COLORS.textMain, mb: 0.5 }}>
            Programa: <b>{solicitudProgDialog?.programa_nombre}</b>
          </Typography>
          <Typography variant="body2" sx={{ color: COLORS.textMuted, mb: 1.5, fontSize: 12 }}>
            {solicitudProgDialog?.tipo === "activar_programa"
              ? "Solicita al administrador reactivar el acceso del usuario a este programa."
              : solicitudProgDialog?.tipo === "inhabilitar_programa"
              ? "Solicita al administrador bloquear el acceso del usuario a este programa."
              : "Solicita al administrador eliminar todo el progreso del usuario en este programa."}
          </Typography>
          <TextField label="Motivo (obligatorio)" multiline rows={3} fullWidth size="small" required
            value={solicitudProgDialog?.motivo || ""}
            onChange={(e) => setSolicitudProgDialog((prev) => ({ ...prev, motivo: e.target.value }))}
            placeholder="Describe el motivo de la solicitud..." error={!!solicitudProgError} />
          {solicitudProgError && <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>{solicitudProgError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => { setSolicitudProgDialog(null); setSolicitudProgError(""); }} disabled={solicitudProgLoading}
            sx={{ textTransform: "none", color: COLORS.textMuted }}>Cancelar</Button>
          <Button variant="contained" onClick={enviarSolicitudPrograma} disabled={solicitudProgLoading}
            sx={{ backgroundColor: "#b00020", "&:hover": { backgroundColor: "#8b0000" },
              textTransform: "none", fontWeight: 700, borderRadius: 999 }}>
            {solicitudProgLoading && <CircularProgress size={14} sx={{ color: "#fff", mr: 1 }} />}
            Enviar solicitud
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}